import { useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Keyboard } from 'lucide-react';
import { useGlobalShortcuts, SHORTCUTS } from '../hooks/useGlobalShortcuts';

/**
 * Componente global que:
 * 1. Registra el listener de atajos vía useGlobalShortcuts().
 * 2. Muestra un indicador flotante (bottom-right) cuando hay un prefijo activo.
 * 3. Abre un modal con la lista completa de atajos al presionar "?".
 * 4. Expone `window.__openShortcutsHelp(true/false)` para apertura programática
 *    desde Cmd+K palette y el dropdown del usuario (evita problemas de focus
 *    al simular pulsaciones de tecla).
 *
 * Se monta una sola vez dentro de <Layout/> (ya hay otros componentes globales allí).
 */
export default function GlobalShortcutsLayer() {
  const { pendingPrefix, showHelp, setShowHelp } = useGlobalShortcuts();

  // Exponer setter para que otros componentes (Cmd+K, dropdown) puedan abrir
  // el modal sin simular eventos de teclado (que se pierden cuando el foco está
  // dentro de cmdk).
  useEffect(() => {
    window.__openShortcutsHelp = (v = true) => setShowHelp(!!v);
    return () => { delete window.__openShortcutsHelp; };
  }, [setShowHelp]);

  const grouped = {};
  for (const sc of SHORTCUTS) {
    const k = sc.keys[0];
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(sc);
  }

  return (
    <>
      {/* Indicador "esperando segunda tecla" */}
      {pendingPrefix && (
        <div
          className="fixed bottom-24 right-6 z-[9999] bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2"
          data-testid="shortcut-pending-indicator"
        >
          <Keyboard className="w-5 h-5 opacity-80" />
          <div>
            <p className="text-xs opacity-70">Esperando segunda tecla...</p>
            <p className="text-base font-mono font-bold">{pendingPrefix.toUpperCase()} + ?</p>
          </div>
        </div>
      )}

      {/* Modal de ayuda con todos los atajos */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="max-w-2xl" data-testid="shortcuts-help-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="w-5 h-5 text-blue-600" />
              Atajos de teclado
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Presiona las teclas en secuencia (sin Ctrl ni Cmd). Tienes 1.5 segundos entre teclas.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 mt-2">
            {Object.entries(grouped).map(([prefix, items]) => (
              <div key={prefix}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-2 mb-1">
                  {prefix === 'g' ? 'Navegación (g + ...)' : 'Crear (c + ...)'}
                </p>
                {items.map((sc) => (
                  <div key={sc.keys.join('')} className="flex items-center justify-between py-1 border-b border-dashed last:border-0 text-xs">
                    <span>{sc.label}</span>
                    <span className="flex gap-0.5">
                      {sc.keys.map((k, i) => (
                        <kbd key={i} className="px-1.5 py-0.5 rounded border border-border bg-muted text-[10px] font-mono uppercase">
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between py-2 border-t mt-2 text-xs">
            <span>Abrir / cerrar esta ayuda</span>
            <kbd className="px-1.5 py-0.5 rounded border bg-muted text-[10px] font-mono">?</kbd>
          </div>
          <div className="flex items-center justify-between py-1 text-xs">
            <span>Búsqueda global / paleta de comandos</span>
            <span className="flex gap-0.5">
              <kbd className="px-1.5 py-0.5 rounded border bg-muted text-[10px] font-mono">⌘/Ctrl</kbd>
              <kbd className="px-1.5 py-0.5 rounded border bg-muted text-[10px] font-mono">K</kbd>
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
